import { Disclosure, Transition } from '@headlessui/react';
import ChevronUpIcon from '@heroicons/react/solid/ChevronUpIcon';
import { SVGProps } from 'react';

import clsxm from '@/lib/clsxm';
import { track } from '@/lib/tracking';
import {
  TrackingEventCategory,
  TrackingEventName,
  TrackingModule,
} from '@/lib/types';
import { useGlobalContext } from '@/hooks/use-global-context';
import useMonitorVisibility from '@/hooks/use-monitor-visibility';

import Typography, { TypographyProps } from '@/components/typography';

export type FaqGroupDesign = 'plain' | 'bordered';
export type FaqGroupIcon = React.FC<SVGProps<SVGSVGElement>>;

export type Faq = {
  id: string;
  title: string;
  content: string;
  defaultOpen?: boolean;
};

export type FaqGroupProps = {
  design?: FaqGroupDesign;
  index?: number;
  title: string;
  subtitle?: string;
  faqs: Faq[];
};

type FaqProps = Pick<Required<FaqGroupProps>, 'title' | 'design'> & {
  content: string;
  open: boolean;
  onClickHeader: () => void;
};

type FaqHeaderProps = Pick<
  Required<FaqProps>,
  'title' | 'open' | 'design' | 'onClickHeader'
>;

type FaqIconProps = Pick<FaqHeaderProps, 'design' | 'open'>;

const FaqGroupContainerClassNameByDesign: Record<FaqGroupDesign, string> = {
  plain: 'items-left',
  bordered: 'items-center',
};

const FaqGroupHeaderTitleComponentByDesign: Record<
  FaqGroupDesign,
  React.FC<TypographyProps>
> = {
  plain: Typography.Heading2,
  bordered: Typography.Heading1,
};

const FaqGroupClassNameByDesign: Record<FaqGroupDesign, string> = {
  plain: 'p-0 sm:pl-14',
  bordered: 'p-0',
};

const FaqContainerClassNameByDesign: Record<FaqGroupDesign, string> = {
  plain: 'border-none',
  bordered: 'border border-grey-300 border-solid rounded-2xl shadow-lg',
};

const FaqHeaderContainerClassNameByDesign: Record<FaqGroupDesign, string> = {
  plain: 'gap-2 flex-row-reverse justify-end',
  bordered: 'justify-between px-6 py-5 sm:px-10 sm:py-5',
};

const FaqHeaderTitleClassNameByDesign: Record<FaqGroupDesign, string> = {
  plain: 'm-0',
  bordered: 'mr-3 leading-[130%] font-medium',
};

const FaqContentContainerClassNameByDesign: Record<FaqGroupDesign, string> = {
  plain: 'pl-12',
  bordered: 'px-6 pb-12 sm:px-10 sm:pb-11',
};

const FaqIconContainerClassNameByDesign: Record<FaqGroupDesign, string> = {
  plain: '',
  bordered: 'rounded-full bg-primary',
};

const iconClassNameByDesign: Record<FaqGroupDesign, string> = {
  plain: '',
  bordered: 'text-white',
};

export const FaqGroupDefaults: Required<Pick<FaqGroupProps, 'design'>> = {
  design: 'plain',
};

export default function FaqGroup(props: FaqGroupProps) {
  const { pageMeta } = useGlobalContext();
  const {
    design = FaqGroupDefaults.design,
    index,
    title,
    subtitle,
    faqs,
  } = props;

  const [ref] = useMonitorVisibility({
    triggerOnce: () => {
      track({
        pageMeta,
        name: TrackingEventName.ModuleViewed,
        category: TrackingEventCategory.ModuleViewed,
        module: TrackingModule.Faq,
        properties: {
          position_number: index,
        },
      });
    },
  });

  const handleClickHeader = () => {
    if (!open) {
      track({
        pageMeta,
        name: TrackingEventName.AccordionToggle,
        category: TrackingEventCategory.FaqComponent,
        module: TrackingModule.Faq,
        properties: {
          position_number: index,
          cta_copy: title,
        },
      });
    }
  };

  const Title = FaqGroupHeaderTitleComponentByDesign[design];

  return (
    <div
      className={clsxm(
        'flex w-full flex-col gap-4 sm:gap-6',
        FaqGroupContainerClassNameByDesign[design]
      )}
    >
      <Title className='text-left'>{title}</Title>

      <span className='sm:hidden'>{subtitle}</span>

      <dl
        ref={ref}
        className={clsxm(
          'flex w-full flex-col gap-4',
          FaqGroupClassNameByDesign[design]
        )}
      >
        {faqs.map(({ id, title, content, defaultOpen }) => (
          <Disclosure
            key={id}
            as='div'
            defaultOpen={defaultOpen ?? true}
            className={FaqContainerClassNameByDesign[design]}
          >
            {({ open }) => (
              <Faq
                title={title}
                design={design}
                open={open}
                onClickHeader={handleClickHeader}
                content={content}
              />
            )}
          </Disclosure>
        ))}
      </dl>
    </div>
  );
}

function Faq({ title, design, open, onClickHeader, content }: FaqProps) {
  return (
    <>
      <FaqHeader
        title={title}
        design={design}
        open={open}
        onClickHeader={onClickHeader}
      />
      <Transition
        unmount={false}
        enter='transition-opacity duration-75'
        enterFrom='opacity-0'
        enterTo='opacity-100'
        leave='transition-opacity duration-150'
        leaveFrom='opacity-100'
        leaveTo='opacity-0'
      >
        <Disclosure.Panel
          as='dd'
          className={FaqContentContainerClassNameByDesign[design]}
          unmount={false}
        >
          <Typography.Body2 className='text-[16px] leading-[150%]'>
            {content ?? ''}
          </Typography.Body2>
        </Disclosure.Panel>
      </Transition>
    </>
  );
}

function FaqHeader({ title, open, design, onClickHeader }: FaqHeaderProps) {
  return (
    <dt>
      <Disclosure.Button
        onClick={onClickHeader}
        className={clsxm(
          'flex w-full items-center text-left',
          FaqHeaderContainerClassNameByDesign[design]
        )}
      >
        <Typography.Heading6
          as='h3'
          weight='normal'
          className={clsxm(
            'flex items-center',
            FaqHeaderTitleClassNameByDesign[design]
          )}
        >
          {title}
        </Typography.Heading6>

        <FaqIcon design={design} open={open} />
      </Disclosure.Button>
    </dt>
  );
}

function FaqIcon({ open, design }: FaqIconProps) {
  return (
    <div
      className={clsxm(
        'flex h-10 w-10 shrink-0 items-center justify-center',
        FaqIconContainerClassNameByDesign[design]
      )}
    >
      <ChevronUpIcon
        aria-hidden={true}
        className={clsxm(
          'h-6 w-6 transition-transform',
          open ? 'rotate-0' : '-rotate-180',
          iconClassNameByDesign[design]
        )}
      />
    </div>
  );
}
